import { ActivityIndicator, Text } from "react-native-paper";
import { ScreenContainer } from "../../components";
import { gql, useQuery } from "@apollo/client";
import { EventDetailsQuery, EventRegistrationQuery, RegistrationStatus } from "./types";
import { TouchableOpacity, View } from "react-native";
import React from "react";
import LottieView from "lottie-react-native";
import { EVENT_DETAILS_FRAGMENT, EVENT_REGISTRATION_QUERY, OFFLINE_FEE_FRAGMENT } from "./queries";
import BarcodeIcon from "../../svgs/BarcodeIcon";
import { ScreenProps } from "../types";
import { SuccessMessage } from "./SuccessMessage";
import { formatCurrency } from "../utils";
import { CURRENT_MEMBER_FRAGMENT } from "../fragments";
import { readableDateWithWeekday } from "../../utils/dateUtils";

export const EVENT_DETAILS_QUERY = gql`
  query EventDetailsQuery($slug: String!) {
    event(slug: $slug) {
      ...EventDetailsFragment
      ...OfflineFeeFragment
    }
    currentMember {
      ...CurrentMemberFragment
    }
  }
  ${EVENT_DETAILS_FRAGMENT}
  ${OFFLINE_FEE_FRAGMENT}
  ${CURRENT_MEMBER_FRAGMENT}
`;

export default function EventConfirmationScreen({ navigation, route }: ScreenProps<"EventConfirmation">) {
  const { loading, data } = useQuery<EventDetailsQuery>(EVENT_DETAILS_QUERY, {
    variables: { slug: route.params.slug },
  });

  const { data: registrationData, loading: registrationLoading } = useQuery<EventRegistrationQuery>(EVENT_REGISTRATION_QUERY, {
    variables: { slug: route.params.slug, status: RegistrationStatus.REGISTERED },
  });

  const registeredSessions = registrationData?.eventRegistration?.sessions ?? [];

  if (loading || registrationLoading) {
    return <ActivityIndicator className="flex-1 items-center justify-center" testID="query-loader" />;
  }

  if (!registrationData?.eventRegistration?.registeredAt) {
    navigation.navigate("EventDetails", { slug: route.params.slug });
  }

  const ConfirmationMessage = () => (
    <>
      <Text className="text-title-2 font-700 mt-6">You're Registered!</Text>
      {data && <SuccessMessage event={data?.event} currentMember={data?.currentMember} />}
    </>
  );

  const RegisteredAttendanceDates = () => (
    <View className="pb-12">
      <Text className="font-700 text-large-tight mt-12 ml-5">Attendance Dates</Text>
      {registeredSessions.length ? (
        <>
          <Text className="text-regular-normal ml-5 pb-3">
            You have registered to attend {registeredSessions.length} {registeredSessions.length === 1 ? "day" : "days"}:
          </Text>

          {registeredSessions.map((session, index) => (
            <Text
              key={session.startsAt}
              className={`text-regular-normal ml-5 mr-5 py-3 ${
                index === registeredSessions.length - 1 ? "" : "border-b border-sky"
              }`}
            >
              {readableDateWithWeekday(session.startsAt, data?.event?.timezone)}
            </Text>
          ))}
        </>
      ) : (
        <Text className="text-regular-normal ml-5 pb-3">No attendance dates available.</Text>
      )}
    </View>
  );

  const OfflineFeeMessage = () => {
    const priceCents = Number(data?.event?.offlineFee?.priceCents);
    const value = priceCents / 100;
    const fee = formatCurrency({ value, unit: "usd" });
    return (
      <Text className="text-regular-normal font-700 text-ink text-center mt-4 px-4">
        {fee} will be due on-site when you arrive for the PAT
      </Text>
    );
  };

  const PrintablePass = () => (
    <>
      {data?.event?.printablePass && (
        <View className="px-4" testID="printable-button">
          <TouchableOpacity
            onPress={() => navigation.navigate("EventTicket", { slug: route.params.slug })}
            className="flex flex-row rounded-lg mb-3 border-sky-dark border h-[188px] lg:h-[148px] self-center py-4 px-6 justify-center items-center"
          >
            <View className="mr-2 max-w-[80%]">
              <Text className="font-700 text-large-tight">Print Your Access Pass</Text>
              <Text className="text-small-normal mt-3">
                Bring your scannable Member Only Access Pass for convenient onsite check-in.
              </Text>
              <Text className="text-link-normal mt-3 text-ink-dark underline">Print It</Text>
            </View>
            <View className="flex-1 ml-4 lg:ml-10">
              <BarcodeIcon />
            </View>
          </TouchableOpacity>
        </View>
      )}
    </>
  );

  const hasOfflineFee = data?.event?.offlineFee;

  return (
    <ScreenContainer>
      <View className="justify-center items-center py-6">
        <View className="w-28 h-28">
          <LottieView style={{ flex: 1 }} autoPlay source={require("../../../assets/lottie-icons/verified.json")} />
        </View>
        <ConfirmationMessage />
        {hasOfflineFee && <OfflineFeeMessage />}
      </View>
      <PrintablePass />
      <RegisteredAttendanceDates />
    </ScreenContainer>
  );
}
